import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Space,
  message,
  Modal,
  Input,
  Tooltip,
  Empty,
  Image,
  Popconfirm,
  Tag,
  Alert,
} from 'antd';
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  LockOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
  LoginOutlined,
} from '@ant-design/icons';
import { copyToClipboard } from '../helpers/clipboard';
import eventBus from '../utils/eventBus';
import { useTranslation } from '../hooks/useTranslation';
import { useUserInfo } from '../contexts/UserInfoContext';
import './EncryptedLinks.css';

const EMPTY_GUIDE_IMAGE_URL = 'https://backup.fukit.cn/autoupload/fdd/20260620/S7FS/854X692/image.png';

interface EncryptedLinkItem {
  id: number;
  linkCode: string;
  uploadId: number;
  remark: string;
  createTime: string;
  accessCount: number;
  lastAccessTime: string | null;
  encryptedUrl: string;
  fileName: string;
  contentType: string;
  thumbnailUrl: string;
}

/**
 * 加密链接管理页面
 * 
 * @author dongdong.fdd
 * @date 2026/01/12
 */
const EncryptedLinks: React.FC = () => {
  const { t } = useTranslation();
  const { isAuthenticated, refresh, status } = useUserInfo();
  const isLoggedIn = status === 'loading' ? null : isAuthenticated;
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<EncryptedLinkItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingLink, setEditingLink] = useState<EncryptedLinkItem | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [newRemark, setNewRemark] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  // 加载数据
  const loadData = useCallback(async () => {
    if (!isLoggedIn) {
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`/e/list?page=${page}&pageSize=${pageSize}`);
      const json = await response.json();

      if (json.data) {
        setData(json.data);
        setTotal(json.total || 0);
      } else if (json.message) {
        message.error(json.message);
      }
    } catch (error) {
      console.error('加载加密链接列表失败:', error);
      message.error(t('encryptedLinks.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, isLoggedIn, t]);

  // 监听页面可见性变化，当用户从登录弹窗返回时重新检查登录状态
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !isLoggedIn) {
        // 页面变为可见时，如果未登录，重新检查登录状态
        void refresh();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isLoggedIn, refresh]);

  // 登录状态确定后才加载数据
  useEffect(() => {
    if (isLoggedIn === true) {
      loadData();
    } else if (isLoggedIn === false) {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, page, pageSize]);

  // 复制链接
  const handleCopy = (url: string) => {
    copyToClipboard(url, t('encryptedLinks.linkCopied'));
  };

  // 删除链接
  const handleDelete = async (linkCode: string) => {
    try {
      const response = await fetch(`/e/delete/${linkCode}`, {
        method: 'DELETE',
      });
      const json = await response.json();

      if (json.success) {
        message.success(t('encryptedLinks.deleteSuccess'));
        loadData();
      } else {
        message.error(json.message || t('encryptedLinks.deleteFailed'));
      }
    } catch (error) {
      console.error('删除失败:', error);
      message.error(t('encryptedLinks.deleteFailedRetry'));
    }
  };

  // 打开编辑弹框
  const handleEdit = (record: EncryptedLinkItem) => {
    setEditingLink(record);
    setNewPassword('');
    setNewRemark(record.remark || '');
    setEditModalOpen(true);
  };

  // 保存编辑
  const handleSaveEdit = async () => {
    if (!editingLink) return;

    setEditLoading(true);
    try {
      const formData = new URLSearchParams();
      if (newPassword) {
        formData.append('password', newPassword);
      }
      formData.append('remark', newRemark);

      const response = await fetch(`/e/update/${editingLink.linkCode}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      const json = await response.json();

      if (json.success) {
        message.success(t('encryptedLinks.updateSuccess'));
        setEditModalOpen(false);
        loadData();
      } else {
        message.error(json.message || t('encryptedLinks.updateFailed'));
      }
    } catch (error) {
      console.error('更新失败:', error);
      message.error(t('encryptedLinks.updateFailedRetry'));
    } finally {
      setEditLoading(false);
    }
  };

  // 格式化时间
  const formatTime = (timeStr: string | null) => {
    if (!timeStr) return '-';
    const date = new Date(timeStr);
    return date.toLocaleString('zh-CN');
  };

  // 表格列定义
  const columns = [
    {
      title: t('encryptedLinks.thumbnail'),
      dataIndex: 'thumbnailUrl',
      key: 'thumbnail',
      width: 80,
      render: (url: string) => (
        url ? (
          <Image
            src={url}
            width={50}
            height={50}
            style={{ objectFit: 'cover', borderRadius: '4px' }}
            fallback="https://z.wiki/autoupload/20230831/l32P.%E5%9B%BE%E7%89%87%E5%8A%A0%E8%BD%BD%E5%A4%B1%E8%B4%A5_%282%29.svg"
          />
        ) : (
          <div style={{ width: 50, height: 50, background: '#f5f5f5', borderRadius: '4px' }} />
        )
      ),
    },
    {
      title: t('encryptedLinks.fileName'),
      dataIndex: 'fileName',
      key: 'fileName',
      ellipsis: true,
      width: 200,
      render: (name: string) => (
        <Tooltip title={name}>
          <span>{name}</span>
        </Tooltip>
      ),
    },
    {
      title: t('encryptedLinks.encryptedLink'),
      dataIndex: 'encryptedUrl',
      key: 'encryptedUrl',
      width: 280,
      render: (url: string) => (
        <Space>
          <a href={url} target="_blank" rel="noopener noreferrer" style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }}>
            {url}
          </a>
          <Tooltip title={t('encryptedLinks.copyLink')}>
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => handleCopy(url)}
            />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: t('encryptedLinks.remark'),
      dataIndex: 'remark',
      key: 'remark',
      width: 150,
      ellipsis: true,
      render: (remark: string) => remark || <span style={{ color: '#999' }}>-</span>,
    },
    {
      title: t('encryptedLinks.accessStats'),
      key: 'stats',
      width: 120,
      render: (_: any, record: EncryptedLinkItem) => (
        <Space direction="vertical" size={0}>
          <Tag color="blue">{t('encryptedLinks.accessCount', { count: record.accessCount })}</Tag>
          {record.lastAccessTime && (
            <span style={{ fontSize: '12px', color: '#999' }}>
              {formatTime(record.lastAccessTime)}
            </span>
          )}
        </Space>
      ),
    },
    {
      title: t('encryptedLinks.createTime'),
      dataIndex: 'createTime',
      key: 'createTime',
      width: 160,
      render: (time: string) => formatTime(time),
    },
    {
      title: t('encryptedLinks.operation'),
      key: 'action',
      width: 120,
      render: (_: any, record: EncryptedLinkItem) => (
        <Space>
          <Tooltip title={t('encryptedLinks.edit')}>
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title={t('encryptedLinks.deleteConfirm')}
            onConfirm={() => handleDelete(record.linkCode)}
            okText={t('common.confirm')}
            cancelText={t('common.cancel')}
          >
            <Tooltip title={t('encryptedLinks.delete')}>
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const creationGuideAlert = (
    <Alert
      className="encrypted-links-guide-alert"
      style={{ marginBottom: 12 }}
      message={t('encryptedLinks.creationGuideDescription')}
      type="info"
      showIcon
    />
  );

  const emptyGuide = (
    <div className="encrypted-links-empty">
      <img
        className="encrypted-links-empty-image"
        src={EMPTY_GUIDE_IMAGE_URL}
        width={854}
        height={692}
        alt={t('encryptedLinks.emptyImageAlt')}
      />
      <div className="encrypted-links-empty-description">
        <div className="encrypted-links-empty-title">
          <LockOutlined />
          <span>{t('encryptedLinks.empty')}</span>
        </div>
        <p>{t('encryptedLinks.emptyDescription')}</p>
      </div>
    </div>
  );

  // 如果正在检查登录状态，显示加载中
  if (isLoggedIn === null) {
    return (
      <div className="encrypted-links-page">
        {creationGuideAlert}
        <div className="page-content">
          <Empty description={t('encryptedLinks.checkingLogin')} style={{ padding: '60px 0' }} />
        </div>
      </div>
    );
  }

  // 如果未登录，显示提示信息
  if (!isLoggedIn) {
    return (
      <div className="encrypted-links-page">
        {creationGuideAlert}
        <div className="page-content">
          <Empty
            description={
              <div>
                <div style={{ marginBottom: '16px', fontSize: '16px', color: '#666' }}>
                  {t('encryptedLinks.loginRequiredMessage')}
                </div>
                <Button
                  type="primary"
                  icon={<LoginOutlined />}
                  onClick={() => eventBus.emit('openLoginModal')}
                >
                  {t('encryptedLinks.loginNow')}
                </Button>
              </div>
            }
            style={{ padding: '60px 0' }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="encrypted-links-page">
      {creationGuideAlert}

      <div className="page-content">
        {data.length === 0 && !loading ? (
          emptyGuide
        ) : (
          <Table
            columns={columns}
            dataSource={data}
            rowKey="id"
            loading={loading}
            pagination={{
              current: page,
              pageSize: pageSize,
              total: total,
              showSizeChanger: true,
              showTotal: (total) => t('encryptedLinks.totalRecords', { total }),
              onChange: (p, ps) => {
                setPage(p);
                setPageSize(ps);
              },
            }}
            scroll={{ x: 1000 }}
          />
        )}
      </div>

      {/* 编辑弹框 */}
      <Modal
        title={t('encryptedLinks.editTitle')}
        open={editModalOpen}
        onCancel={() => setEditModalOpen(false)}
        footer={
          <Space>
            <Button onClick={() => setEditModalOpen(false)}>{t('encryptedLinks.cancel')}</Button>
            <Button type="primary" onClick={handleSaveEdit} loading={editLoading}>
              {t('encryptedLinks.save')}
            </Button>
          </Space>
        }
      >
        <div style={{ padding: '12px 0' }}>
          <div style={{ marginBottom: '16px' }}>
            <div style={{ marginBottom: '8px', fontWeight: 500 }}>{t('encryptedLinks.fileName')}</div>
            <Input value={editingLink?.fileName} disabled />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <div style={{ marginBottom: '8px', fontWeight: 500 }}>
              {t('encryptedLinks.passwordLabel')}
            </div>
            <Input.Password
              placeholder={t('encryptedLinks.passwordPlaceholder')}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              iconRender={(visible) =>
                visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
              }
            />
          </div>

          <div>
            <div style={{ marginBottom: '8px', fontWeight: 500 }}>{t('encryptedLinks.remarkLabel')}</div>
            <Input
              placeholder={t('encryptedLinks.remarkPlaceholder')}
              value={newRemark}
              onChange={(e) => setNewRemark(e.target.value)}
              maxLength={200}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default EncryptedLinks;
